// Decodes one kissaderby.fi /catderby WebSocket message (base64) into a SignalFrame. Pure.
// Format: .taiga/knowledge/landscape/kissaderby-positioning-signal.md
//   byte 0: state = b % 3 (0 starting, 1 running, 2 finished), ham lane = ⌊b/3⌋ % 6, catnip lane = ⌊b/18⌋
//   running (19 bytes): six u16 cat words (low 10 bits position 0..1023, +1024 ham, +2048 catnip),
//                       u16 ham position (0 = being eaten), u16 catnip position (0 = someone is tripping)
//   finished (13 bytes): six u16 times in tenths of a second from post time, +32768 = DSQ
import type { Phase, SignalFrame } from '../race/types';

export const POS_MAX = 1023;
const PHASES: Phase[] = ['starting', 'running', 'finished'];
const MIN_BYTES: Record<Phase, number> = { starting: 1, running: 17, finished: 13 };

function toBytes(b64: string): string | null {
  try {
    return atob(b64.trim());
  } catch {
    return null;
  }
}

/** Returns null for anything that is not a well-formed binary frame (including the site's old text format). */
export function decode(b64: string): SignalFrame | null {
  if (!b64 || !/^[A-Za-z0-9+/]+={0,2}$/.test(b64.trim())) return null; // also rejects the old multi-line text format
  const s = toBytes(b64);
  if (!s || s.length === 0) return null;
  const u8 = (i: number) => s.charCodeAt(i);
  const u16 = (i: number) => u8(i) * 256 + u8(i + 1);
  const f = u8(0);
  const state = PHASES[f % 3]!;
  if (s.length < MIN_BYTES[state]) return null;
  if (state === 'starting') return { state };

  if (state === 'running') {
    const hamLane = Math.floor(f / 3) % 6;
    const nipLane = Math.floor(f / 18);
    const cats: NonNullable<SignalFrame['cats']> = [];
    for (let i = 0; i < 6; i++) {
      const v = u16(1 + 2 * i);
      cats.push({ pos: Math.min(POS_MAX, v & 1023) / POS_MAX, ham: (v & 1024) !== 0, nip: (v & 2048) !== 0 });
    }
    const hp = u16(13) & 1023;
    const np = u16(15) & 1023;
    return {
      state, cats,
      ham: hp ? { lane: hamLane, pos: hp / POS_MAX } : null,
      catnip: np && nipLane < 6 ? { lane: nipLane, pos: np / POS_MAX } : null,
    };
  }

  const times: NonNullable<SignalFrame['times']> = [];
  for (let i = 0; i < 6; i++) {
    const v = u16(1 + 2 * i);
    times.push({ seconds: (v & 0x7fff) / 10, dsq: (v & 0x8000) !== 0 });
  }
  return { state, times };
}

/** Raw byte length of a message, for diagnostics and tests. */
export function byteLength(b64: string): number {
  return toBytes(b64)?.length ?? 0;
}
